/**
 * UPT-2096 (P1-a, §4.6) — vue d'un inventaire : par écran (libellé, type de
 * pièce, stats), titres / navigation / actions en listes, formulaires
 * (tableau des champs), tableaux (colonnes, nombre de lignes) ; chaque
 * tableau HTML a une <caption> ; défilement interne sur mobile. Tout est
 * rendu en enfants texte React (jamais de HTML ni de Markdown).
 */
import { useIntl } from '@/react-intl';
import type { PrototypeImageRef, PrototypeInventory, PrototypeMenu, PrototypeMenuPosition, PrototypePiece, PrototypeScreen, PrototypeSection } from '../data/contracts';
import PrototypeStyleView from './PrototypeStyleView';

export interface PrototypeInventoryViewProps {
  inventory: PrototypeInventory;
  pieces?: PrototypePiece[];
  /** L2 : nom de fichier par rang de source quand les pièces manquent (aperçu du dialogue). */
  sourceFileNames?: string[];
}

// i18n Rule 1 : glyphes techniques (séparateurs, flèches, tirets), pas du texte utilisateur — hissés au niveau du module.
const DOT = ' · ';
const ARROW = '→ ';
const EMPTY = '—';
const LIST_SEPARATOR = ', ';

function SectionsList({ sections, t }: { sections: PrototypeSection[]; t: (id: string) => string }) {
  if (sections.length === 0) return null;
  return (
    <div className="mb-3">
      <h4 className="text-sm font-medium text-portal-ink-muted">{t('moduleStudio.prototype.inventory.sections')}</h4>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-portal-ink">
        {sections.map((section) => (
          <li key={section.selector} className="flex flex-wrap items-baseline gap-2" style={{ paddingInlineStart: `${section.depth}rem` }}>
            <code dir="ltr" className="rounded bg-portal-surface-muted px-1 text-xs">{section.selector}</code>
            {section.label && <span>{section.label}</span>}
            {section.hidden && <span className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.inventory.sectionHidden')}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const menuPositionLabel = (t: (id: string) => string, position: PrototypeMenuPosition): string => {
  switch (position) {
    case 'left': return t('moduleStudio.prototype.inventory.menu.position.left');
    case 'top': return t('moduleStudio.prototype.inventory.menu.position.top');
    case 'bottom': return t('moduleStudio.prototype.inventory.menu.position.bottom');
    default: return t('moduleStudio.prototype.inventory.menu.position.unknown');
  }
};

/** F2 : le menu du prototype tel que l'inventaire v3 le porte (groupes, ordre, séparateurs, genre de chaque entrée). Texte seulement. */
function MenuView({ menu, t }: { menu: PrototypeMenu; t: (id: string) => string }) {
  return (
    <section aria-labelledby="inventory-menu-heading" className="rounded-md border border-portal-border bg-portal-surface p-4">
      <h3 id="inventory-menu-heading" className="mb-3 text-base font-semibold text-portal-ink">
        {t('moduleStudio.prototype.inventory.menu.title')}
        {DOT}
        <span className="text-sm font-normal text-portal-ink-muted">{menuPositionLabel(t, menu.position)}</span>
      </h3>
      {menu.groups.map((group, groupIndex) => (
        <div key={groupIndex} className="mb-3">
          <h4 className="text-sm font-medium text-portal-ink-muted">{group.label ?? t('moduleStudio.prototype.inventory.menu.noLegend')}</h4>
          <ol className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-portal-ink">
            {group.items.map((item, itemIndex) => (
              <li key={itemIndex} className="flex flex-wrap items-baseline gap-2">
                {item.separatorBefore && <hr className="m-0 w-full border-portal-border" aria-label={t('moduleStudio.prototype.inventory.menu.separator')} />}
                <span className="font-medium">{item.label ?? EMPTY}</span>
                {item.kind === 'dialog' && <span className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.inventory.menu.dialog')}</span>}
                {item.kind === 'action' && <span className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.inventory.menu.action')}</span>}
                {item.icon && <code dir="ltr" className="rounded bg-portal-surface-muted px-1 text-xs">{item.icon}</code>}
                {item.entry && <code dir="ltr" className="break-all text-xs text-portal-ink-muted">{item.entry}</code>}
                {!item.screenRef && <span className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.inventory.menu.noScreen')}</span>}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}

function ImagesList({ images, t }: { images: PrototypeImageRef[]; t: (id: string) => string }) {
  if (images.length === 0) return null;
  const sourceLabel = (image: PrototypeImageRef): string => {
    switch (image.source) {
      case 'data': return t('moduleStudio.prototype.inventory.imageData');
      case 'remote': return t('moduleStudio.prototype.inventory.imageRemote');
      case 'blob': return t('moduleStudio.prototype.inventory.imageBlob');
      default: return t('moduleStudio.prototype.inventory.imageRelative');
    }
  };
  return (
    <div className="mb-3">
      <h4 className="text-sm font-medium text-portal-ink-muted">{t('moduleStudio.prototype.inventory.images')}</h4>
      <ul className="list-inside list-disc text-sm text-portal-ink">
        {images.map((image, index) => (
          <li key={index}>
            {sourceLabel(image)}
            {image.mediaType && <>{DOT}<code dir="ltr" className="text-xs">{image.mediaType}</code></>}
            {image.host && <>{DOT}<code dir="ltr" className="text-xs">{image.host}</code></>}
            {image.alt && <>{DOT}{image.alt}</>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PrototypeInventoryView({ inventory, pieces = [], sourceFileNames }: PrototypeInventoryViewProps) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const pieceOf = (ref: string): PrototypePiece | undefined => pieces.find((piece) => piece.id === ref);

  if (inventory.screens.length === 0) {
    return <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.inventory.empty')}</p>;
  }

  const renderScreen = (screen: PrototypeScreen) => {
    const piece = pieceOf(screen.sourcePieceRef);
    return (
      <section key={screen.sourcePieceRef} className="rounded-md border border-portal-border bg-portal-surface p-4">
        <header className="mb-3 flex flex-wrap items-baseline gap-2">
          <h3 className="text-base font-semibold text-portal-ink">{screen.label}</h3>
          {screen.entry && <code dir="ltr" className="max-w-full break-all text-xs text-portal-ink-muted">{screen.entry.label}<br />{screen.entry.selector}</code>}
          {piece && (
            <span className="text-xs text-portal-ink-muted">
              {piece.kind === 'html'
                ? t('moduleStudio.prototype.inventory.pieceHtml')
                : t('moduleStudio.prototype.inventory.pieceSnapshot')}
            </span>
          )}
          <span className="text-xs text-portal-ink-muted">
            {screen.stats.elementCount} {t('moduleStudio.prototype.inventory.elements')}
            {DOT}
            {screen.stats.textLength} {t('moduleStudio.prototype.inventory.characters')}
          </span>
        </header>

        {screen.headings.length > 0 && (
          <div className="mb-3">
            <h4 className="text-sm font-medium text-portal-ink-muted">{t('moduleStudio.prototype.inventory.headings')}</h4>
            <ul className="list-inside list-disc text-sm text-portal-ink">
              {screen.headings.map((heading, index) => <li key={index}>{heading}</li>)}
            </ul>
          </div>
        )}
        {screen.navigation.length > 0 && (
          <div className="mb-3">
            <h4 className="text-sm font-medium text-portal-ink-muted">{t('moduleStudio.prototype.inventory.navigation')}</h4>
            <ul className="list-inside list-disc text-sm text-portal-ink">
              {screen.navigation.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          </div>
        )}
        {screen.actions.length > 0 && (
          <div className="mb-3">
            <h4 className="text-sm font-medium text-portal-ink-muted">{t('moduleStudio.prototype.inventory.actions')}</h4>
            <ul className="list-inside list-disc text-sm text-portal-ink">
              {screen.actions.map((action, index) => <li key={index}>{action}</li>)}
            </ul>
          </div>
        )}

        <SectionsList sections={screen.sections ?? []} t={t} />
        <ImagesList images={screen.images ?? []} t={t} />

        {screen.forms.map((form, formIndex) => (
          <div key={formIndex} className="mb-3">
            <h4 className="text-sm font-medium text-portal-ink">
              {form.title ?? t('moduleStudio.prototype.inventory.untitledForm')}
              {form.submitLabel && (
                <span className="ms-2 text-xs text-portal-ink-muted">{ARROW}{form.submitLabel}</span>
              )}
              {form.geodeEntity && (
                <code dir="ltr" className="ms-2 rounded bg-portal-surface-muted px-1 text-xs">{form.geodeEntity}</code>
              )}
            </h4>
            <div className="mt-1 overflow-x-auto">
              <table className="min-w-full text-sm">
                <caption className="sr-only">{form.title ?? t('moduleStudio.prototype.inventory.untitledForm')}</caption>
                <thead>
                  <tr className="border-b border-portal-border text-start text-xs text-portal-ink-muted">
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldLabel')}</th>
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldName')}</th>
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldType')}</th>
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldRequired')}</th>
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldOptions')}</th>
                    <th className="pe-3 py-1 text-start">{t('moduleStudio.prototype.inventory.fieldPlaceholder')}</th>
                    <th className="py-1 text-start">{t('moduleStudio.prototype.inventory.fieldGeode')}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.fields.map((field, fieldIndex) => (
                    <tr key={fieldIndex} className="border-b border-portal-border">
                      <td className="pe-3 py-1 text-portal-ink">{field.label ?? EMPTY}</td>
                      <td className="pe-3 py-1 text-portal-ink">{field.name ?? EMPTY}</td>
                      <td className="pe-3 py-1 text-portal-ink">{field.type}</td>
                      <td className="pe-3 py-1 text-portal-ink">
                        {field.required ? t('moduleStudio.prototype.inventory.yes') : t('moduleStudio.prototype.inventory.no')}
                      </td>
                      <td className="pe-3 py-1 text-portal-ink">{field.options.join(LIST_SEPARATOR) || EMPTY}</td>
                      <td className="pe-3 py-1 text-portal-ink">{field.placeholder ?? EMPTY}</td>
                      <td className="py-1 text-portal-ink">{field.geodeField ?? EMPTY}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {screen.tables.map((table, tableIndex) => (
          <div key={tableIndex} className="mb-3">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <caption className="text-start text-sm font-medium text-portal-ink">
                  {table.title ?? t('moduleStudio.prototype.inventory.untitledTable')}
                  {table.geodeEntity && (
                    <code dir="ltr" className="ms-2 rounded bg-portal-surface-muted px-1 text-xs">{table.geodeEntity}</code>
                  )}
                </caption>
                <thead>
                  <tr className="border-b border-portal-border text-xs text-portal-ink-muted">
                    {table.columns.map((column, columnIndex) => (
                      <th key={columnIndex} className="pe-3 py-1 text-start">{column}</th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
            <p className="text-xs text-portal-ink-muted">
              {table.rowCount} {t('moduleStudio.prototype.inventory.rows')}
            </p>
          </div>
        ))}
      </section>
    );
  };

  const sources = inventory.sources ?? [];
  if (sources.length < 2) {
    return (
      <div className="flex flex-col gap-6">
        {sources[0] && inventory.version === 2 ? <PrototypeStyleView style={sources[0].style} /> : null}
        {inventory.menu ? <MenuView menu={inventory.menu} t={t} /> : null}
        {inventory.screens.map(renderScreen)}
      </div>
    );
  }

  // L2 : un groupe par source ; un écran appartient à la source de sa pièce (html ou capture), à défaut à la source 0.
  const rankByPiece = new Map(pieces.map((piece) => [piece.id, piece.sourceIndex ?? 0]));
  const grouped = new Map<number, PrototypeScreen[]>(sources.map((source) => [source.sourceIndex, []]));
  for (const screen of inventory.screens) {
    const direct = sources.find((source) => source.pieceRef === screen.sourcePieceRef);
    const rank = direct ? direct.sourceIndex : rankByPiece.get(screen.sourcePieceRef) ?? sources[0].sourceIndex;
    (grouped.get(rank) ?? grouped.get(sources[0].sourceIndex))!.push(screen);
  }
  return (
    <div className="flex flex-col gap-8">
      {inventory.menu ? <MenuView menu={inventory.menu} t={t} /> : null}
      {sources.map((source) => {
        const fileName = pieceOf(source.pieceRef)?.fileName ?? sourceFileNames?.[source.sourceIndex] ?? '';
        const headingId = `inventory-source-${source.sourceIndex}`;
        const screens = grouped.get(source.sourceIndex) ?? [];
        return (
          <section key={source.pieceRef} aria-labelledby={headingId} className="flex flex-col gap-4">
            <h3 id={headingId} className="text-lg font-semibold text-portal-ink">
              {intl.formatMessage(
                { id: 'moduleStudio.prototype.inventory.source' },
                { n: source.sourceIndex + 1, fileName }
              )}
              {source.title && <>{DOT}{source.title}</>}
              {source.lang && <>{DOT}<span dir="ltr">{source.lang}</span></>}
            </h3>
            {inventory.version === 2 ? <PrototypeStyleView style={source.style} /> : null}
            {screens.length === 0
              ? <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.inventory.empty')}</p>
              : screens.map(renderScreen)}
          </section>
        );
      })}
    </div>
  );
}
